<!-- Hero Section -->
<section class="gpu-hero relative text-white overflow-hidden py-20 md:py-32">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-chip mr-2"></i>NVIDIA CERTIFIED
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Cloud <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">GPUs</span>
        </h1>
        
        <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0">
          Run GPU-intensive workloads with confidence using our Cloud GPU services. Get access to NVIDIA H200, H100, A100, L40S, L4 and more to accelerate your AI/ML workloads.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-chip mr-2"></i>
            Launch GPU Instance
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto lg:mx-0">
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">10x</div>
            <div class="text-sm text-blue-200">Faster Training</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">Performance</div>
            <div class="text-sm text-blue-200">Optimized</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">99.99%</div>
            <div class="text-sm text-blue-200">Uptime SLA</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/gpu-cloud-hero.png" alt="GPU Cloud" class="w-full h-auto object-cover">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-chip text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">NVIDIA</div>
              <div class="text-xs">Accelerators</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-bolt text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">AI/ML</div>
              <div class="text-xs">Optimized</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- GPU Models Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/70">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-500/10 border border-blue-500/40 text-blue-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        GPU Models
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Available <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">GPU Models</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Choose from the latest NVIDIA GPUs optimized for AI, machine learning, and high-performance computing workloads.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- NVIDIA H200 -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-cyan-500/70 hover:border-cyan-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-400/60">LATEST</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA H200</h3>
        <p class="text-slate-300 mb-4">
          The world's most powerful accelerator for AI and HPC with 141GB HBM3e memory.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">141GB HBM3e Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">4.8TB/s Memory Bandwidth</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">FP8 Precision Support</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Advanced Transformer Engine</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-cyan-300">High-Performance GPU</span>
        </div>
      </div>

      <!-- NVIDIA H100 -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-blue-500/70 hover:border-blue-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-400/60">POPULAR</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA H100</h3>
        <p class="text-slate-300 mb-4">
          Exceptional performance for large-scale AI training and inference workloads.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">80GB HBM3 Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">3TB/s Memory Bandwidth</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">4th Gen Tensor Cores</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">NVLink 4.0 Support</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-blue-300">Enterprise AI Accelerator</span>
        </div>
      </div>

      <!-- NVIDIA A100 -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-indigo-500/70 hover:border-indigo-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-indigo-500/20 text-indigo-200 text-xs font-bold px-3 py-1 rounded-full border border-indigo-400/60">PROVEN</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA A100</h3>
        <p class="text-slate-300 mb-4">
          Proven performance for AI training, inference, and scientific computing.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">40GB/80GB HBM2e Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">3rd Gen Tensor Cores</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Multi-Instance GPU</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">PCIe Gen4 Support</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-indigo-300">Scalable AI Solution</span>
        </div>
      </div>

      <!-- NVIDIA L40S -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-purple-500/70 hover:border-purple-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full border border-purple-400/60">UNIVERSAL</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA L40S</h3>
        <p class="text-slate-300 mb-4">
          Universal GPU for AI inference, 3D graphics, and video processing.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">48GB GDDR6 Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Ada Lovelace Architecture</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">RT Cores for Ray Tracing</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">AV1 Encode/Decode</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-purple-300">Graphics & Inference GPU</span>
        </div>
      </div>

      <!-- NVIDIA L4 -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-pink-500/70 hover:border-pink-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-pink-500/20 text-pink-200 text-xs font-bold px-3 py-1 rounded-full border border-pink-400/60">EFFICIENT</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA L4</h3>
        <p class="text-slate-300 mb-4">
          Efficient GPU for AI inference and lightweight training workloads.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">24GB GDDR6 Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Energy Efficient Design</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Video Processing Engines</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Cost-Effective Solution</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-pink-300">Entry-Level AI GPU</span>
        </div>
      </div>

      <!-- NVIDIA RTX 4090 -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 border border-rose-500/70 hover:border-rose-400 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <span class="bg-rose-500/20 text-rose-200 text-xs font-bold px-3 py-1 rounded-full border border-rose-400/60">DEVELOPMENT</span>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">NVIDIA RTX 4090</h3>
        <p class="text-slate-300 mb-4">
          High-performance GPU for development and creative workloads.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">24GB GDDR6X Memory</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Ada Lovelace Architecture</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">DLSS 3 Support</span>
          </li>
          <li class="flex items-center text-slate-300">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Ray Tracing Cores</span>
          </li>
        </ul>
        <div class="mt-4">
          <span class="text-sm font-bold text-rose-300">Development GPU</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Pre-configured Frameworks Section -->
<section class="py-20 bg-slate-950 border-t border-slate-800/70">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        AI/ML Frameworks
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Pre-configured <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-400">AI/ML Frameworks</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Our GPU instances come pre-installed with popular AI/ML frameworks and libraries for immediate productivity.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- TensorFlow -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 transform hover:-translate-y-2 text-center border border-blue-500/60 hover:border-blue-400">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bxl-tensorflow text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">TensorFlow</h3>
        <p class="text-slate-300 mb-4">
          Google's open-source machine learning framework with GPU acceleration.
        </p>
        <span class="inline-block bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-400/60">Google</span>
      </div>

      <!-- PyTorch -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 transform hover:-translate-y-2 text-center border border-indigo-500/60 hover:border-indigo-400">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bxl-python text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">PyTorch</h3>
        <p class="text-slate-300 mb-4">
          Facebook's dynamic neural network framework with CUDA support.
        </p>
        <span class="inline-block bg-indigo-500/20 text-indigo-200 text-xs font-bold px-3 py-1 rounded-full border border-indigo-400/60">Meta</span>
      </div>

      <!-- Jupyter Lab -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 transform hover:-translate-y-2 text-center border border-cyan-500/60 hover:border-cyan-400">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-code text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">Jupyter Lab</h3>
        <p class="text-slate-300 mb-4">
          Interactive development environment for data science and machine learning.
        </p>
        <span class="inline-block bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-400/60">Development</span>
      </div>

      <!-- CUDA Toolkit -->
      <div class="bg-slate-900/80 rounded-2xl shadow-2xl p-8 hover:shadow-[0_20px_45px_rgba(15,23,42,0.9)] transition-all duration-300 transform hover:-translate-y-2 text-center border border-purple-500/60 hover:border-purple-400">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-chip text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-100 mb-3">CUDA Toolkit</h3>
        <p class="text-slate-300 mb-4">
          NVIDIA's parallel computing platform and programming model.
        </p>
        <span class="inline-block bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full border border-purple-400/60">NVIDIA</span>
      </div>
    </div>
  </div>
</section>

<!-- Use Cases Section -->
<section class="py-20 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Application Scenarios
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        GPU Cloud <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">Use Cases</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Perfect for a wide range of AI, ML, and high-performance computing applications.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      <!-- AI Model Training -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-cyan-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-brain text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-cyan-100 text-cyan-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">AI</span>
            <h3 class="text-2xl font-bold text-gray-900">AI Model Training</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Train large language models, computer vision models, and deep neural networks with accelerated performance.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Large Language Models</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Computer Vision</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Deep Neural Networks</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Accelerated Performance</span>
          </li>
        </ul>
      </div>

      <!-- Inference & Deployment -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-blue-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-data text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">Inference</span>
            <h3 class="text-2xl font-bold text-gray-900">Inference & Deployment</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Deploy trained models for real-time inference with low latency and high throughput.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Real-time Inference</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Low Latency</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>High Throughput</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Model Serving</span>
          </li>
        </ul>
      </div>

      <!-- Scientific Computing -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-indigo-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-calculator text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">HPC</span>
            <h3 class="text-2xl font-bold text-gray-900">Scientific Computing</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Accelerate scientific simulations, computational fluid dynamics, and molecular modeling.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Scientific Simulations</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Computational Fluid Dynamics</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Molecular Modeling</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>HPC Workloads</span>
          </li>
        </ul>
      </div>

      <!-- Data Analytics -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-purple-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-bar-chart text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">Analytics</span>
            <h3 class="text-2xl font-bold text-gray-900">Data Analytics</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Process large datasets with GPU-accelerated analytics and visualization tools.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>GPU-Accelerated Analytics</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Data Visualization</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Large Dataset Processing</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-cyan-600 mr-3 text-xl"></i>
            <span>Real-time Insights</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Accelerate Your AI Workloads?
      </h2>
      <p class="text-xl text-blue-100 mb-10">
        Get started with our Cloud GPUs and experience the power of NVIDIA's latest accelerators. Start building your AI applications today with our expert support team.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-cyan-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-chip mr-2 text-xl"></i>
          <span class="text-lg">Launch GPU Instance</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-headphone mr-2 text-xl"></i>
          <span class="text-lg">Talk to Sales Team</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">10x</div>
          <div class="text-blue-200 text-sm">Faster Training</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-blue-200 text-sm">Expert Support</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">99.99%</div>
          <div class="text-blue-200 text-sm">Uptime SLA</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Free</div>
          <div class="text-blue-200 text-sm">Consultation</div>
        </div>
      </div>
    </div>
  </div>
</section>
